import { Info } from "lucide-react";
import React, { useState, ReactNode } from "react";
import { createPortal } from "react-dom"; // Import createPortal

interface TooltipProps {
  text: string | React.ReactNode;
  position?: "top" | "bottom" | "left" | "right";
  children: ReactNode;
  delay?: number;
  className?: string;
  tooltipClassName?: string;
  note?: ReactNode;
}

const Tooltip: React.FC<TooltipProps> = ({
  note,
  text,
  position = "top",
  children,
  delay = 200,
  className = "",
  tooltipClassName = "",
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  const handleMouseEnter = (e: React.MouseEvent) => {
    const targetRect = e.currentTarget.getBoundingClientRect();

    // Calculate position based on the specified position prop
    let top = 0;
    let left = 0;

    switch (position) {
      case "top":
        top = targetRect.top - 15;
        left = targetRect.left + targetRect.width / 2.2;
        break;
      case "bottom":
        top = targetRect.bottom + 10;
        left = targetRect.left + targetRect.width / 2;
        break;
      case "left":
        top = targetRect.top + targetRect.height / 2;
        left = targetRect.left - 10;
        break;
      case "right":
        top = targetRect.top + targetRect.height / 2;
        left = targetRect.right + 10;
        break;
    }

    setTooltipPosition({ top, left });

    const id = setTimeout(() => {
      setIsVisible(true);
    }, delay);
    setTimeoutId(id);
  };

  const handleMouseLeave = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    setIsVisible(false);
  };

  // Format text with line breaks if it's a string and contains numbers
  const formattedText =
    typeof text === "string"
      ? text.split(/(\d+\.\s)/).map((part, index) => {
          if (/^\d+\.\s$/.test(part)) {
            return index > 1 ? (
              <React.Fragment key={index}>
                <br />
                {part}
              </React.Fragment>
            ) : (
              <React.Fragment key={index}>{part}</React.Fragment>
            );
          }
          return <React.Fragment key={index}>{part}</React.Fragment>;
        })
      : text;

  // Position classes for the tooltip
  const getPositionStyles = () => {
    switch (position) {
      case "top":
        return {
          top: `${tooltipPosition.top}px`,
          left: `${tooltipPosition.left}px`,
          transform: "translate(-50%, -100%)",
        };
      case "bottom":
        return {
          top: `${tooltipPosition.top}px`,
          left: `${tooltipPosition.left}px`,
          transform: "translate(-50%, 0)",
        };
      case "left":
        return {
          top: `${tooltipPosition.top}px`,
          left: `${tooltipPosition.left}px`,
          transform: "translate(-100%, -50%)",
        };
      case "right":
        return {
          top: `${tooltipPosition.top}px`,
          left: `${tooltipPosition.left}px`,
          transform: "translate(0, -50%)",
        };
    }
  };

  return (
    <div className="col-span-1">
      <div
        className={`relative flex gap-2 items-center cursor-pointer ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div>
          <Info size={16} className="text-primary" />
        </div>

        <div>{children}</div>

        {/* Render tooltip using a portal */}
        {isVisible &&
          createPortal(
            <div
              className="fixed"
              style={{
                ...getPositionStyles(),
                zIndex: 2147483647, // Maximum possible z-index
              }}
            >
              <div
                className={`bg-[#fff] w-[500px] text-zinc-500 shadow-2xl relative border text-xs rounded-xl p-3 leading-relaxed ${tooltipClassName}`}
              >
                {note && (
                  <div className="mb-3 bg-zinc-100 p-3 text-xs rounded-lg ">
                    {note}
                  </div>
                )}
                {formattedText}
              </div>
            </div>,
            document.body, // Render at body level
          )}
      </div>
    </div>
  );
};

export default Tooltip;
